import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Select,
  MenuItem,
  Typography,
  Tooltip,
  Chip,
  FormControl,
  InputLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Info as InfoIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import MessageTemplateService from '../../services/messageTemplateService';

// Constants from Django model
const CHANNEL_CHOICES = {
  sms: 'SMS',
  email: 'Email',
  voice: 'Voice',
  chat: 'Chat',
};

const CATEGORY_CHOICES = {
  awareness: 'Awareness',
  interest: 'Interest',
  decision: 'Decision',
  conversion: 'Conversion',
  lost: 'Lost',
};

const EditTemplateDialog = ({ 
  open, 
  onClose, 
  template,
  onTemplateUpdated,
  loading,
  setLoading,
  showSnackbar,
}) => {
  const [editedTemplate, setEditedTemplate] = useState({
    name: "",
    content: "",
    category: "awareness",
    channel: "sms",
    account: '',
  });
  const [variableCategories, setVariableCategories] = useState([]);

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (open && template) {
      setEditedTemplate({
        name: template.name || "",
        content: template.content || "",
        category: template.category || "awareness",
        channel: template.channel || "sms",
        account: template.account || "",
      });

      setEditedTemplate({
        name: template.name || "",
        content: template.content || "",
        category: template.category || "awareness",
        channel: template.channel || "sms",
        account: template.account || "",
      });
      fetchAvailableVariables();
    } else {
      setEditedTemplate({
        name: "",
        content: "",
        category: "awareness",
        channel: "sms",
        account: "",
      });
    }
  }, [open, template]);

  const fetchAvailableVariables = async () => {
    try {
      const categories = await MessageTemplateService.getAvailableVariables();
      setVariableCategories(categories);
    } catch (err) {
      console.error('Failed to fetch available variables:', err);
      showSnackbar('Failed to fetch available variables', 'error');
    }
  };

  const handleUpdateTemplate = async () => {
    try {
      setLoading(true);
      const updatedTemplate = await MessageTemplateService.updateTemplate(template.id, editedTemplate);
      onTemplateUpdated(updatedTemplate);
      onClose();
      showSnackbar('Template updated successfully');
    } catch (err) {
      console.error('Failed to update template:', err);
      showSnackbar('Failed to update template', 'error');
    } finally {
      setLoading(false);
    }
  };

  const insertVariable = (categoryName, variableName) => {
    const variable = `{{${categoryName}.${variableName}}}`;
    setEditedTemplate(prev => ({
      ...prev,
      content: prev.content + variable
    }));
  };

  const renderVariableList = () => {
    return variableCategories.map((category) => (
      <Accordion key={category.name}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls={`${category.name}-content`}
          id={`${category.name}-header`}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="subtitle2">
              {category.name}
            </Typography>
            <Tooltip title={category.description}>
              <InfoIcon fontSize="small" color="action" />
            </Tooltip>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {category.variables.map((variable) => (
              <Tooltip 
                key={variable.name} 
                title={
                  <Box>
                    <Typography variant="body2">{variable.description}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Field: {variable.field_name}
                    </Typography>
                  </Box>
                }
              >
                <Chip
                  label={`{{${category.name}.${variable.name}}}`}
                  size="small"
                  onClick={() => insertVariable(category.name, variable.name)}
                  sx={{ 
                    '&:hover': {
                      backgroundColor: 'primary.light',
                      color: 'primary.contrastText'
                    }
                  }}
                />
              </Tooltip>
            ))}
          </Box>
        </AccordionDetails>
      </Accordion>
    ));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Template</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <TextField
            label="Name"
            value={editedTemplate.name}
            onChange={(e) => {
              setEditedTemplate({ ...editedTemplate, name: e.target.value });
            }}
            fullWidth
            required
          />
          <FormControl fullWidth>
            <InputLabel>Category</InputLabel>
            <Select
              value={editedTemplate.category}
              onChange={(e) => {
                setEditedTemplate({ ...editedTemplate, category: e.target.value });
              }}
              label="Category"
              required
            >
              {Object.entries(CATEGORY_CHOICES).map(([value, label]) => (
                <MenuItem key={value} value={value}>{label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>Channel</InputLabel>
            <Select
              value={editedTemplate.channel}
              onChange={(e) => {
                setEditedTemplate({ ...editedTemplate, channel: e.target.value });
              }}
              label="Channel"
              required
            >
              {Object.entries(CHANNEL_CHOICES).map(([value, label]) => (
                <MenuItem key={value} value={value}>{label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Message Template
            </Typography>
            <TextField
              multiline
              rows={4}
              value={editedTemplate.content}
              onChange={(e) => {
                setEditedTemplate({ ...editedTemplate, content: e.target.value });
              }}
              placeholder="Enter your template content. Use {{variable}} for personalization."
              fullWidth
              required
            />
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                Available Variables
                <Tooltip title="Click on a variable to insert it into your template">
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </Typography>
              {renderVariableList()}
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button 
          onClick={handleUpdateTemplate} 
          variant="contained"
          disabled={loading || !editedTemplate.name || !editedTemplate.content}
        >
          Update Template
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditTemplateDialog; 